import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { JOB_STATES, JOB_STATE_AR } from "@fanni/shared/job-state";
import { PRIORITY_AR, STATUS_TONE, fmtDateTime } from "../lib/format.js";
import { useBoard, useCustomers, useJobs } from "../lib/queries.js";
import type { JobFilters } from "../lib/queries.js";
import { today } from "../lib/format.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from "../ui/kit.js";

const PAGE = 50;

export function Jobs() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");

  const filters: JobFilters = useMemo(
    () => ({
      status: params.get("status") ?? undefined,
      technician_id: params.get("technician_id") ?? undefined,
      customer_id: params.get("customer_id") ?? undefined,
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      limit: PAGE,
      offset,
    }),
    [params, offset],
  );

  const { data, isLoading, error } = useJobs(filters);
  const customers = useCustomers();
  // The board is the only endpoint that lists technicians to a dispatcher — /users is
  // owner+manager only, and a filter that 403s for half the office is not a filter.
  const board = useBoard(today());

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setOffset(0);
  };

  /**
   * Search filters the fetched page, client-side, and says so — GET /jobs has no `q`
   * parameter, so a search box that implied it queried the whole table would quietly lie on
   * page 2 of a company with 4,000 jobs.
   */
  const rows = useMemo(() => {
    const all = data?.jobs ?? [];
    const q = search.trim();
    if (!q) return all;
    return all.filter((j) =>
      [j.title, j.customerName, j.siteLabel, j.technicianName, j.number ? `#${j.number}` : ""]
        .filter(Boolean)
        .some((v) => String(v).includes(q)),
    );
  }, [data, search]);

  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="المهام"
        subtitle={
          <>
            <span className="num">{total}</span> مهمة مطابقة للفلاتر.
          </>
        }
      />

      <Card className="mb-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Field label="الحالة">
            <Select value={params.get("status") ?? ""} onChange={(e) => setFilter("status", e.target.value)}>
              <option value="">الكل</option>
              {JOB_STATES.map((s) => (
                <option key={s} value={s}>
                  {JOB_STATE_AR[s]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="الفني">
            <Select
              value={params.get("technician_id") ?? ""}
              onChange={(e) => setFilter("technician_id", e.target.value)}
            >
              <option value="">الكل</option>
              {(board.data?.columns ?? []).map((c) => (
                <option key={c.technician.id} value={c.technician.id}>
                  {c.technician.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="الزبون">
            <Select
              value={params.get("customer_id") ?? ""}
              onChange={(e) => setFilter("customer_id", e.target.value)}
            >
              <option value="">الكل</option>
              {(customers.data?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="من تاريخ">
            <Input type="date" value={params.get("from") ?? ""} onChange={(e) => setFilter("from", e.target.value)} />
          </Field>

          <Field label="إلى تاريخ">
            <Input type="date" value={params.get("to") ?? ""} onChange={(e) => setFilter("to", e.target.value)} />
          </Field>

          <Field label="بحث" hint="داخل النتائج المعروضة فقط">
            <Input
              placeholder="زبون، موقع، عنوان، رقم…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
        </div>

        {params.size > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            className="mt-3"
            onClick={() => {
              setParams(new URLSearchParams(), { replace: true });
              setOffset(0);
            }}
          >
            مسح الفلاتر
          </Button>
        ) : null}
      </Card>

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      <Card bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>لا توجد مهام مطابقة.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الرقم</Th>
                <Th>الحالة</Th>
                <Th>الزبون</Th>
                <Th>الموقع</Th>
                <Th>العنوان</Th>
                <Th>النوع</Th>
                <Th>الفني</Th>
                <Th>الأولوية</Th>
                <Th>موعدها</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <Tr key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}>
                  <Td>
                    <span className="num text-ink-soft">{j.number ? `#${j.number}` : "—"}</span>
                  </Td>
                  <Td>
                    <Badge tone={STATUS_TONE[j.status]}>{JOB_STATE_AR[j.status]}</Badge>
                  </Td>
                  <Td className="font-medium">{j.customerName ?? "—"}</Td>
                  <Td className="text-ink-soft">{j.siteLabel ?? "—"}</Td>
                  <Td className="max-w-64 truncate">{j.title}</Td>
                  <Td className="text-ink-soft">{j.jobTypeNameAr ?? "—"}</Td>
                  <Td>{j.technicianName ?? <span className="text-warn">غير مُسندة</span>}</Td>
                  <Td>
                    {j.priority === "normal" ? (
                      <span className="text-ink-faint">—</span>
                    ) : (
                      <Badge tone={j.priority === "emergency" ? "bad" : j.priority === "high" ? "warn" : "neutral"}>
                        {PRIORITY_AR[j.priority]}
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <span className="num text-ink-soft">{fmtDateTime(j.scheduledFor)}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}

        {total > PAGE ? (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2">
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>
              السابق
            </Button>
            <span className="num text-xs text-ink-faint">
              {offset + 1}–{Math.min(offset + PAGE, total)} من {total}
            </span>
            <Button size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset((o) => o + PAGE)}>
              التالي
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}
